import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Wifi, BatteryCharging, Lock, Mountain, Wine, Sparkles, ArrowUpRight, Expand, X, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const featureIcons = {
  locker: <Lock size={14} />,
  balcony: <Mountain size={14} />,
  charging: <BatteryCharging size={14} />,
  wifi: <Wifi size={14} />,
  bar: <Wine size={14} />,
};

const iconFor = (text) => {
  const t = text.toLowerCase();
  if (t.includes("locker")) return featureIcons.locker;
  if (t.includes("balcony") || t.includes("view") || t.includes("mountain") || t.includes("terrace") || t.includes("sunrise")) return featureIcons.balcony;
  if (t.includes("charging")) return featureIcons.charging;
  if (t.includes("wi-fi") || t.includes("wifi")) return featureIcons.wifi;
  if (t.includes("bar") || t.includes("sake")) return featureIcons.bar;
  return <Sparkles size={14} className="text-[#f5f0ea]" />;
};

export default function RoomsSection() {
  const [rooms, setRooms] = useState([]);

  useEffect(() => {
    axios.get(`${API}/rooms`).then((r) => setRooms(r.data)).catch(() => setRooms([]));
  }, []);

  const privates = rooms.filter((r) => r.category === "private");
  const socials = rooms.filter((r) => r.category === "social");

  return (
    <section id="rooms" data-testid="rooms-section" className="relative py-24 md:py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          eyebrow="Where you'll stay"
          title="Pick your room. Find your tribe."
          subtitle="16 rooms. Mountain views. Private or shared — your call."
        />

        <Group title="Private Stays" tagline="For couples, scenic lovers, and premium chillers." testid="private-stays">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {privates.map((r, idx) => (
              <RoomCard key={r.id} room={r} index={idx} />
            ))}
          </div>
        </Group>

        <Group title="Social Stays" tagline="Dorms with private locker, balcony view & instant friends." testid="social-stays">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {socials.map((r, idx) => (
              <RoomCard key={r.id} room={r} index={idx} />
            ))}
          </div>
        </Group>
      </div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="max-w-3xl mb-10 md:mb-14"
    >
      <div className="text-xs uppercase tracking-[0.22em] text-[#f5f0ea] font-semibold">{eyebrow}</div>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 tracking-tighter text-white">
        {title}
      </h2>
      {subtitle && <p className="mt-4 text-neutral-400 text-base sm:text-lg">{subtitle}</p>}
    </motion.div>
  );
}

function Group({ title, tagline, testid, children }) {
  return (
    <div className="mb-16 last:mb-0" data-testid={testid}>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h3 className="font-display text-2xl sm:text-3xl font-semibold text-white">{title}</h3>
          <p className="text-sm text-neutral-400 mt-1">{tagline}</p>
        </div>
        <div className="hidden sm:block divider w-40" />
      </div>
      {children}
    </div>
  );
}

function RoomCard({ room, index }) {
  const gallery = room.images && room.images.length ? room.images : [room.image];
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const handleBook = () => {
    window.dispatchEvent(
      new CustomEvent("prefill-booking", { detail: { roomType: room.id } })
    );
    const el = document.getElementById("booking");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, delay: index * 0.06 }}
      whileHover={{ y: -6 }}
      className="group relative rounded-3xl overflow-hidden bg-neutral-900 border border-white/5 hover:border-[#A7D38B]/40 transition-colors"
      data-testid={`room-card-${room.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          key={gallery[activeImg]}
          src={gallery[activeImg]}
          alt={room.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />

        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          data-testid={`room-image-${room.id}`}
          aria-label={`View ${room.name} photos full screen`}
          className="absolute inset-0 z-[6] flex items-center justify-center cursor-zoom-in"
        >
          <span className="opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300 w-12 h-12 rounded-full glass flex items-center justify-center text-white">
            <Expand size={18} />
          </span>
        </button>

        {room.badge && (
          <span
            data-testid={`room-badge-${room.id}`}
            className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-[#EF4444]/90 text-white text-[11px] font-semibold px-3 py-1.5 animate-pulseGlow z-10"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-white" />
            {room.badge}
          </span>
        )}

        <span className="absolute top-3 right-3 rounded-full glass text-[11px] font-medium text-[#A7D38B] px-3 py-1.5 z-10">
          {room.tag}
        </span>

        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between z-10 pointer-events-none">
          <div>
            <div className="font-display font-semibold text-white text-xl drop-shadow">{room.name}</div>
            <div className="text-[11px] text-neutral-300">{room.best_for}</div>
          </div>
          <div className="text-right">
            <div className="font-display font-bold text-2xl text-[#A7D38B]">₹{room.price.toLocaleString()}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-300">
              per {room.price_unit}
            </div>
          </div>
        </div>
      </div>

      <div className="p-5">
        {gallery.length > 1 && (
          <div className="flex gap-2 mb-4" data-testid={`room-gallery-${room.id}`}>
            {gallery.map((src, i) => (
              <button
                key={src}
                type="button"
                onMouseEnter={() => setActiveImg(i)}
                onClick={() => setActiveImg(i)}
                data-testid={`room-thumb-${room.id}-${i}`}
                className={`relative h-12 w-16 shrink-0 rounded-lg overflow-hidden border transition ${
                  activeImg === i ? "border-[#A7D38B]" : "border-white/10 hover:border-white/30"
                }`}
              >
                <img src={src} alt={`${room.name} view ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-1.5 mb-4">
          {room.features.slice(0, 4).map((f) => (
            <span
              key={f}
              className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] border border-white/10 text-[11px] text-neutral-300 px-2.5 py-1"
            >
              {iconFor(f)}
              {f}
            </span>
          ))}
        </div>

        <button
          onClick={handleBook}
          data-testid={`room-book-${room.id}`}
          className="inline-flex items-center justify-center w-full gap-2 rounded-xl bg-[#62794d] text-[#f5f0ea] font-semibold py-2.5 text-sm hover:bg-[#7a9261] transition group/btn"
        >
          Book {room.name}
          <ArrowUpRight size={16} className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition" />
        </button>
      </div>

      <AnimatePresence>
        {lightboxOpen && (
          <Lightbox
            room={room}
            images={gallery}
            index={activeImg}
            setIndex={setActiveImg}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </AnimatePresence>
    </motion.article>
  );
}

function Lightbox({ room, images, index, setIndex, onClose }) {
  const go = useCallback(
    (dir) => setIndex((i) => (i + dir + images.length) % images.length),
    [images.length, setIndex]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [go, onClose]);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[120] flex flex-col bg-black/90 backdrop-blur-xl"
      onClick={onClose}
      data-testid={`lightbox-${room.id}`}
    >
      <div className="flex items-center justify-between px-5 sm:px-8 py-5" onClick={(e) => e.stopPropagation()}>
        <div>
          <div className="font-display text-white text-lg sm:text-xl font-semibold">{room.name}</div>
          <div className="text-[11px] text-neutral-400">
            {index + 1} / {images.length}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid={`lightbox-close-${room.id}`}
          className="w-11 h-11 rounded-full glass flex items-center justify-center text-white hover:bg-white/10 transition"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      <div className="relative flex-1 flex items-center justify-center px-4 sm:px-16 pb-6 min-h-0" onClick={(e) => e.stopPropagation()}>
        <motion.img
          key={images[index]}
          src={images[index]}
          alt={`${room.name} view ${index + 1}`}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25 }}
          className="max-h-full max-w-full object-contain rounded-xl"
        />

        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              data-testid={`lightbox-prev-${room.id}`}
              className="absolute left-3 sm:left-6 w-12 h-12 rounded-full glass flex items-center justify-center text-white hover:bg-white/10 transition"
              aria-label="Previous photo"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              data-testid={`lightbox-next-${room.id}`}
              className="absolute right-3 sm:right-6 w-12 h-12 rounded-full glass flex items-center justify-center text-white hover:bg-white/10 transition"
              aria-label="Next photo"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="flex justify-center gap-2 px-4 pb-6 flex-wrap" onClick={(e) => e.stopPropagation()}>
          {images.map((src, i) => (
            <button
              key={src}
              type="button"
              onClick={() => setIndex(i)}
              data-testid={`lightbox-thumb-${room.id}-${i}`}
              className={`h-14 w-20 shrink-0 rounded-lg overflow-hidden border-2 transition ${
                index === i ? "border-[#A7D38B]" : "border-white/10 hover:border-white/40 opacity-70 hover:opacity-100"
              }`}
            >
              <img src={src} alt={`${room.name} thumbnail ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </motion.div>,
    document.body
  );
}
